import React, { useId } from "react";
import { createPortal } from "react-dom";
import Modal from "@mui/material/Modal";

// Shared portal modal shell (Phase A promotion, module 11 polish pass). Donor:
// ChamberSettings SettingsModal (createPortal + SSR guard, z-[9999] overlay,
// max-w-xl panel with "max-w-" suppression, round bordered X). Also the
// promotion target for the hand-rolled UnsavedChanges-style overlays
// (overlayClassName="bg-black/40", titleless panels).
//
// Legacy callers keep their existing focus and Escape behavior. Email popups
// can opt into MUI focus management and Escape through their own close guard.
// Backdrop clicks do not close either mode.
// ConfirmDialog stays on MUI Dialog; SettingsModal itself migrates to a thin
// wrapper over this shell in a later phase (untouched in Phase A).

export type ModalShellProps = {
  open: boolean;
  onClose: () => void;
  /** Renders the SettingsModal header row when provided */
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** default: true iff title provided; renders the round "X" */
  showCloseButton?: boolean;
  /** aria-labelledby target; auto-generated id applied to title when omitted */
  labelledById?: string;
  /** Reports an in-progress modal action to assistive technology. */
  ariaBusy?: boolean;
  /** Opt-in focus containment, focus return, and Escape through onClose. */
  enableKeyboardFocus?: boolean;
  /** default "bg-black/45"; UnsavedChanges-style callers pass "bg-black/40" */
  overlayClassName?: string;
  /** default "max-w-xl" applied only when no "max-w-" present (donor behavior) */
  panelClassName?: string;
  bodyClassName?: string;
  children: React.ReactNode;
};

export const ModalShell: React.FC<ModalShellProps> = ({
  open,
  onClose,
  title,
  description,
  showCloseButton,
  labelledById,
  ariaBusy,
  enableKeyboardFocus = false,
  overlayClassName = "bg-black/45",
  panelClassName = "",
  bodyClassName = "",
  children,
}) => {
  const autoTitleId = useId();

  if (!open) {
    return null;
  }

  const hasTitle = title !== undefined && title !== null;
  const shouldShowClose = showCloseButton ?? hasTitle;
  const hasHeader = hasTitle || shouldShowClose;
  const titleId = labelledById ?? (hasTitle ? autoTitleId : undefined);
  const hasCustomMaxWidth = panelClassName.includes("max-w-");

  const modalContent = (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center ${overlayClassName} p-4`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={ariaBusy}
        className={`max-h-[calc(100vh-2rem)] w-full ${
          hasCustomMaxWidth ? "" : "max-w-xl"
        } overflow-y-auto rounded-lg border border-gray-200 bg-white p-6 shadow-xl ${panelClassName}`}
      >
        {hasHeader ? (
          <div className="flex items-start justify-between gap-4">
            <div>
              {hasTitle ? (
                <h3 id={titleId} className="text-lg font-bold text-black">
                  {title}
                </h3>
              ) : null}
              {description !== undefined && description !== null ? (
                <p className="mt-1 text-sm leading-6 text-black">
                  {description}
                </p>
              ) : null}
            </div>
            {shouldShowClose ? (
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-sm font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                aria-label="Close modal"
              >
                X
              </button>
            ) : null}
          </div>
        ) : null}
        <div className={`${hasHeader ? "mt-5 " : ""}${bodyClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return modalContent;
  }

  if (enableKeyboardFocus) {
    return (
      <Modal
        open={open}
        hideBackdrop
        disableScrollLock
        sx={{ zIndex: 9999 }}
        onClose={(_, reason) => {
          if (reason === "escapeKeyDown") onClose();
        }}
      >
        {modalContent}
      </Modal>
    );
  }

  return createPortal(modalContent, document.body);
};
